import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import useFetchInterval from "src/ui/hooks/useFetchInterval";

import { useFetchProductBuildsQuery } from "@dashboard/products/api";
import { Build } from "@dashboard/products/types";
import { Product } from "@dashboard/products/types/index";
import { SectionHeader } from "@dashboard/shared/styles";
import { Box, Paper, TableContainer, TableRow, Typography, useTheme } from "@mui/material";
import SemverService from "@services/semverService/semverService";
import { MinimalTable, MinimalTableCell, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import {
    createColumnHelper,
    flexRender,
    functionalUpdate,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    Updater,
    useReactTable,
} from "@tanstack/react-table";

import { useBuildsTableStyles } from "./BuildsTable.style";
import { ArtifactsCell, BuildActionsCell, ChannelCell, StatusCell, TagsCell, TimestampCell } from "./Cells";
import { isBuildProcessing } from "./isBuildProcessing";
import { useBuildStatusPulse } from "./useBuildStatusPulse";

const columnHelper = createColumnHelper<Build>();

type ProductProps = {
    product: Product;
};
const BuildsTable = (props: ProductProps) => {
    const [sorting, setSorting] = React.useState<SortingState>([
        { id: "version", desc: true },
        { id: "created", desc: true },
    ]);

    const theme = useTheme();
    const { t } = useTranslation(["common", "pages"]);
    const { classes } = useBuildsTableStyles();

    const { data = [], isLoading, isError, refetch } = useFetchProductBuildsQuery(props.product.id);

    const hasProcessingBuilds = data.some(isBuildProcessing);

    useFetchInterval(refetch, { condition: hasProcessingBuilds });

    const { isPulsing, onPulseEnd } = useBuildStatusPulse(data);

    const onSortingChange = (updater: Updater<SortingState>) => {
        const newValue = functionalUpdate(updater, sorting);
        setSorting(newValue);
    };

    const tableColumns = React.useMemo(
        () => [
            columnHelper.accessor("buildStatus", {
                header: "Status",
                cell: (info) => (
                    <StatusCell buildStatus={info.getValue()} build={info.row.original} productId={props.product.id} />
                ),
                enableSorting: true,
                meta: { align: "center" },
            }),
            columnHelper.accessor("version", {
                header: "Version",
                cell: (info) => (
                    <MinimalTableCell width={100}>
                        <Typography>{info.getValue()}</Typography>
                    </MinimalTableCell>
                ),
                enableSorting: true,
                sortingFn: (rowA, rowB) => SemverService.compare(rowA.original.version, rowB.original.version),
            }),
            columnHelper.accessor("created", {
                header: t("common:label.date"),
                cell: (info) => <TimestampCell created={info.getValue()} finished={info.row.original.built} />,
                enableSorting: true,
            }),
            columnHelper.accessor("artifacts", {
                header: "Artifacts",
                cell: (info) => <ArtifactsCell build={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.accessor("metadata", {
                id: "tags",
                header: t("common:label.tags"),
                cell: (info) => <TagsCell build={info.row.original} />,
                enableSorting: false,
            }),
            columnHelper.accessor("channel", {
                header: "Channel",
                cell: (info) => <ChannelCell build={info.row.original} />,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (info) => <BuildActionsCell build={info.row.original} product={props.product} />,
                meta: { align: "right" },
            }),
        ],
        [props.product],
    );

    const table = useReactTable<Build>({
        data,
        columns: tableColumns,
        state: { sorting },
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        onSortingChange: onSortingChange,
    });

    return (
        <Box>
            <SectionHeader style={{ marginBottom: theme.spacing(2), marginTop: theme.spacing(8) }}>
                {t("pages:productPage.listOfBuilds")}
            </SectionHeader>
            <TableContainer component={Paper}>
                <MinimalTable>
                    <MinimalTableHeader>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody
                        table={table}
                        loading={isLoading}
                        noDataMessage={t("pages:productPage.clickBuildToCreateNewRelease")}
                        error={{ isError, onError: refetch }}
                    >
                        {table.getRowModel().rows.map((row) => (
                            <TableRow
                                key={row.id}
                                className={clsx(isPulsing(row.original.buildId) && classes.pulseRow)}
                                onAnimationEnd={(e) => {
                                    // ignore bubbling animationend from any nested element
                                    if (e.target === e.currentTarget) {
                                        onPulseEnd(row.original.buildId);
                                    }
                                }}
                            >
                                {row.getVisibleCells().map((cell) => (
                                    <React.Fragment key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </React.Fragment>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
        </Box>
    );
};

export default BuildsTable;
